import { useEffect, useState, type ReactNode } from 'react';

/**
 * Client-side auth gate. The SERVER is the source of truth — this only
 * decides which screen to show based on what `/api/me` returns:
 *   200 → render the app (children), passing the verified user down
 *   401 → "Sign in with Google"
 *   403 → "Not authorised" (signed in, but not on the allowlist)
 *   other / network error → retryable error screen
 * It never holds data; a non-allowlisted user gets no payload from any
 * /api route regardless of what this renders.
 */

export type AuthUser = {
  email: string;
  name?: string;
  role?: 'admin' | 'member';
  /** Set while an admin is viewing the app as this person: the admin. */
  viewed_by?: { email: string; name: string };
};

type AuthState =
  | { kind: 'loading' }
  | { kind: 'authenticated'; user: AuthUser }
  | { kind: 'unauthenticated' }
  | { kind: 'unauthorised' }
  | { kind: 'error'; message: string };

// Codes the server's callback handler appends as ?auth_error=… on redirect
// (see server/src/auth/google-auth.ts).
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  not_authorised: "That Google account isn't allowed to use this app.",
  callback_failed: "Sign-in didn't complete. Please try again.",
  invalid_state: "Sign-in didn't complete (session expired). Please try again.",
  login_failed: 'Sign-in failed to start. Please try again.',
};

function readAuthErrorParam(): string | null {
  const url = new URL(window.location.href);
  const value = url.searchParams.get('auth_error');
  if (value !== null) {
    url.searchParams.delete('auth_error');
    window.history.replaceState({}, '', url.toString());
  }
  return value;
}

export function startLogin() {
  window.location.assign('/api/auth/login');
}

export async function signOut() {
  try {
    await fetch('/api/auth/logout', { method: 'POST' });
  } finally {
    window.location.assign('/');
  }
}

function LoginScreen({ banner }: { banner: string | null }) {
  return (
    <div className="min-h-full flex items-center justify-center px-6">
      <div className="card p-8 max-w-sm w-full text-center space-y-4">
        <div>
          <div className="text-lg font-semibold tracking-tight">
            Wakeflow · Dealflow
          </div>
          <div className="text-xs text-ink-300 mt-1">Sign in to continue</div>
        </div>
        {banner ? (
          <div className="text-xs text-negative-500 border border-negative-500/30 rounded-md p-2">
            {banner}
          </div>
        ) : null}
        <button
          onClick={startLogin}
          className="w-full text-sm font-medium px-3 py-2 rounded-md bg-positive-500 text-ink-900 hover:bg-positive-500/90"
        >
          Sign in with Google
        </button>
      </div>
    </div>
  );
}

function NotAuthorisedScreen({ banner }: { banner: string | null }) {
  return (
    <div className="min-h-full flex items-center justify-center px-6">
      <div className="card p-8 max-w-sm w-full text-center space-y-4">
        <div>
          <div className="text-lg font-semibold tracking-tight text-negative-500">
            Not authorised
          </div>
          <div className="text-xs text-ink-300 mt-1">
            Your Google account isn't permitted to use this app.
          </div>
        </div>
        {banner ? (
          <div className="text-xs text-negative-500 border border-negative-500/30 rounded-md p-2">
            {banner}
          </div>
        ) : null}
        <button
          onClick={startLogin}
          className="w-full text-sm font-medium px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 text-ink-100"
        >
          Try a different account
        </button>
        <button
          onClick={signOut}
          className="w-full text-sm font-medium px-3 py-2 rounded-md text-ink-300 hover:text-ink-100"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function ErrorScreen({ message }: { message: string }) {
  return (
    <div className="min-h-full flex items-center justify-center px-6">
      <div className="card p-8 max-w-sm w-full text-center space-y-3">
        <div className="text-lg font-semibold text-negative-500">
          Couldn't reach the server
        </div>
        <div className="text-xs text-ink-300 break-words">{message}</div>
        <button
          onClick={() => window.location.reload()}
          className="w-full text-sm font-medium px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 text-ink-100"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

export function RequireAuth({
  children,
}: {
  children: (user: AuthUser) => ReactNode;
}) {
  const [state, setState] = useState<AuthState>({ kind: 'loading' });
  const [banner, setBanner] = useState<string | null>(null);

  useEffect(() => {
    const errCode = readAuthErrorParam();
    if (errCode) {
      setBanner(AUTH_ERROR_MESSAGES[errCode] ?? `Sign-in error: ${errCode}`);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/me');
        if (cancelled) return;
        if (res.status === 200) {
          const body = (await res.json()) as AuthUser;
          setState({ kind: 'authenticated', user: body });
        } else if (res.status === 401) {
          setState({ kind: 'unauthenticated' });
        } else if (res.status === 403) {
          setState({ kind: 'unauthorised' });
        } else {
          setState({ kind: 'error', message: `/api/me returned ${res.status}` });
        }
      } catch (e) {
        if (cancelled) return;
        setState({ kind: 'error', message: String(e) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === 'loading') {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="text-sm text-ink-300">Loading…</div>
      </div>
    );
  }
  if (state.kind === 'unauthenticated') return <LoginScreen banner={banner} />;
  if (state.kind === 'unauthorised') return <NotAuthorisedScreen banner={banner} />;
  if (state.kind === 'error') return <ErrorScreen message={state.message} />;
  return <>{children(state.user)}</>;
}
